import {
  useDeleteInterviewMutation,
  useGetAllInterviewsQuery,
} from "@/app/features/recruitment/interview/interview.api";
import ConfirmationModal from "@/components/ConfirmationModal";
import CustomTable from "@/components/CustomTable";
import StatusTag from "@/components/StatusTag";
import { DISCLOSER_KEYS, FILTER_KEYS } from "@/constants/keys.constants";
import { TrashIcon } from "@/icons";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import { Button, Space, type TableProps } from "antd";
import { useAppDiscloser, useAppFilters } from "../../../../hooks";
import RescheduleInterview from "./_components/RescheduleInterview";
import SetupInterview from "./_components/SetupInterview";
import { formatDate, formatTo12Hour, normalizeColumnValue } from "@/utils";

const INTERVIEW_STATUS_CONFIG = [
  { label: "Passed", value: "passed", className: "status status-success" },
  { label: "Failed", value: "failed", className: "status status-error" },
  { label: "No-Show", value: "no_show", className: "status status-error" },
  { label: "Pending", value: "pending", className: "status status-yellow" },
  {
    label: "In Progress",
    value: "in_progress",
    className: "status status-yellow",
  },
];

export default function ScheduledInterviews() {
  const { page, limit, keyword, filters, handlePagination } = useAppFilters(
    FILTER_KEYS.INTERVIEW_SCHEDULED,
  );

  const [config, handleOpen, handleClose] = useAppDiscloser<"RESCHEDULE">(
    DISCLOSER_KEYS.SCHEDULED_INTERVIEW,
  );

  const { data: interviewsData, isFetching } = useGetAllInterviewsQuery({
    page,
    limit,
    search: keyword,
    ...filters,
  });

  const [deleteInterview, { isLoading }] = useDeleteInterviewMutation();

  const columns: TableProps<any>["columns"] = [
    {
      title: "Applicant",
      dataIndex: "applicant_name",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Job Applied",
      dataIndex: "job_title",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Interview Method",
      dataIndex: "interview_type",
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },
    {
      title: "Interviewer",
      dataIndex: "evaluator_name",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Date & Time",
      dataIndex: "interview_date",
      align: "center",
      render: (val: string, record: any) => {
        const date = formatDate(val);
        const time = formatTo12Hour(record.interview_time);

        return (
          <span>
            {date}
            {" - "}
            {time}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "interview_status",
      align: "center",
      render: (val: string) => (
        <StatusTag value={val} config={INTERVIEW_STATUS_CONFIG} />
      ),
    },
    {
      title: "Reschedule",
      dataIndex: "interview_status",
      align: "center",
      render: (val, record) => (
        <span>
          {val === "no_show" ? (
            <Button
              type="default"
              className="text-[#00C6AE] border-none !bg-transparent
              shadow-none underline underline-offset-4 decoration-[#00C6AE] p-0"
              onClick={() =>
                handleOpen({ type: "RESCHEDULE", data: { id: record.id } })
              }
            >
              Reschedule
            </Button>
          ) : (
            <Button
              disabled
              type="default"
              className="!text-[#979797] border-none !bg-transparent
              shadow-none underline underline-offset-4 decoration-[#979797] p-0"
            >
              Reschedule
            </Button>
          )}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "end",
      render: (id: string) => (
        <Space size={8}>
          <Button
            size="small"
            className="action-btn"
            icon={<TrashIcon size={20} />}
            onClick={() => handleOpen({ type: "DELETE", data: { id } })}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={interviewsData?.data?.data || []}
        onPaginate={handlePagination}
        customTitle="Scheduled Interviews"
        tableRootClassName="border-0 p-0"
        loading={isFetching}
        total={interviewsData?.data?.meta?.total || 0}
      />

      <SetupInterview
        open={config.open && config.type === "ADD"}
        handleClose={handleClose}
      />

      <RescheduleInterview
        id={config.data?.id}
        open={config.open && config.type === "RESCHEDULE"}
        handleClose={handleClose}
      />

      <ConfirmationModal
        open={config.open && config.type === "DELETE"}
        handleClose={handleClose}
        loading={isLoading}
        handleSubmit={async () => {
          if (!config.data?.id) return;
          try {
            const res = await deleteInterview({ id: config.data.id }).unwrap();
            if (res?.success === false) {
              toast(
                "error",
                getErrorMessage(res, "Failed to delete interview"),
              );
              return;
            }
            toast(
              "success",
              getErrorMessage(res, "Interview deleted successfully"),
            );
            handleClose();
          } catch (error) {
            toast(
              "error",
              getErrorMessage(error, "Failed to delete interview"),
            );
          }
        }}
        title="Delete Interview"
        subTitle="Are you sure you want to delete this interview? This action cannot be undone."
        buttonName="Delete"
      />
    </div>
  );
}
